/**
 * Video-Settings-Helper (Welle 5).
 *
 * Erweitert um:
 *  - masterTrackCameras: pro Stele konfigurierbar welche Kameras im
 *    Master-Track-Modus laufen (Default: ['doccam'])
 *  - resolveMasterTrackForRole() Helper
 */

export interface ResolutionPreset {
  width: number;
  height: number;
}

export const RESOLUTIONS: Record<string, ResolutionPreset> = {
  '240p': { width: 426, height: 240 },
  '360p': { width: 640, height: 360 },
  '480p': { width: 854, height: 480 },
  '720p': { width: 1280, height: 720 },
  '1080p': { width: 1920, height: 1080 },
  '4k': { width: 3840, height: 2160 },
};

/** Constraints mit 'ideal'.
 *  preserveAspect=true (Welle 8c-pre45): nur die kurze Seite (Höhe bei Landscape-
 *  Webcams) vorgeben und die Breite frei lassen → die Kamera liefert ihr NATIVES
 *  Seitenverhältnis. Sonst zwingt ein fixes width×height den Sensor in 16:9 und
 *  beschneidet bei 4:3-Sensoren das Sichtfeld zu einem Querformat-Streifen. */
export function buildVideoConstraints(
  deviceId: string,
  resolutionKey: string,
  fps: number,
  preserveAspect = false,
): MediaTrackConstraints {
  const res = RESOLUTIONS[resolutionKey] ?? RESOLUTIONS['720p'];
  if (preserveAspect) {
    const shortSide = Math.min(res.width, res.height);
    return {
      deviceId: { exact: deviceId },
      height: { ideal: shortSide },
      frameRate: { ideal: fps, max: fps + 5 },
    };
  }
  return {
    deviceId: { exact: deviceId },
    width: { ideal: res.width },
    height: { ideal: res.height },
    frameRate: { ideal: fps, max: fps + 5 },
  };
}

/**
 * Versucht die Kamera in max-möglicher Auflösung zu öffnen.
 * Probiert eine absteigende Liste von Kandidaten durch.
 */
export async function openMasterStream(
  deviceId: string,
  fps: number,
  candidates: ResolutionPreset[] = [
    { width: 3840, height: 2160 },  // 4K
    { width: 3264, height: 2448 },  // 8MP
    { width: 2592, height: 1944 },  // 5MP
    { width: 2048, height: 1536 },  // 3MP
    { width: 1920, height: 1080 },  // 1080p Fallback
    { width: 1280, height: 720 },   // 720p letzter Fallback
  ],
): Promise<MediaStream> {
  let lastErr: any = null;
  for (const cand of candidates) {
    // Welle 8c-pre6: Pro Auflösung zuerst strikte fps-Begrenzung versuchen (begrenzt
    // die USB-2.0-MJPEG-Bandbreite → keine schwarzen Frames). Nur falls die Kamera diese
    // fps-Stufe bei dieser Auflösung NICHT anbietet, fps lockern — aber die AUFLÖSUNG
    // niemals aufgeben (Scans brauchen das volle Bild). Erst danach nächste Auflösung.
    const fpsAttempts: MediaTrackConstraints['frameRate'][] = [
      { ideal: fps, max: fps },        // strikt: Bandbreite deckeln
      { ideal: fps, max: fps + 5 },    // leicht gelockert
      { ideal: fps },                  // fps egal, Auflösung halten
    ];
    for (const frameRate of fpsAttempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: cand.width },
            height: { ideal: cand.height },
            frameRate,
          },
        });
        const settings = stream.getVideoTracks()[0].getSettings();
        console.log(
          `[masterStream] geöffnet ${settings.width}x${settings.height}@${settings.frameRate} (wollte ${cand.width}x${cand.height}, fps-Ziel ${fps})`,
        );
        return stream;
      } catch (e) {
        lastErr = e;
        // nächste fps-Variante bei gleicher Auflösung
      }
    }
  }
  throw lastErr || new Error('Keine Master-Auflösung verfügbar');
}

export async function applyResolution(
  track: MediaStreamTrack,
  resolutionKey: string,
  fps: number,
): Promise<{ width: number; height: number; frameRate: number }> {
  const res = RESOLUTIONS[resolutionKey] ?? RESOLUTIONS['720p'];
  try {
    await track.applyConstraints({
      width: { ideal: res.width },
      height: { ideal: res.height },
      frameRate: { ideal: fps, max: fps + 5 },
    });
  } catch (e: any) {
    console.warn('[videoSettings] applyConstraints schlug fehl:', e?.message);
  }
  const s = track.getSettings();
  return {
    width: s.width ?? res.width,
    height: s.height ?? res.height,
    frameRate: s.frameRate ?? fps,
  };
}

export function getActualTrackSettings(track: MediaStreamTrack): {
  width: number;
  height: number;
  frameRate: number;
} {
  const s = track.getSettings();
  return {
    width: s.width ?? 0,
    height: s.height ?? 0,
    frameRate: s.frameRate ?? 0,
  };
}

export type CameraRole = 'webcam' | 'doccam';

export interface VideoSettings {
  profile: string;
  mainResolution: string;
  previewResolution: string;
  mainFps: number;
  previewFps: number;
  hasPreview: boolean;
  webcamLabel: string | null;
  docCamLabel: string | null;
  audioInputLabel: string | null;
  /** Liste der Kamera-Rollen, die im Master-Track-Modus laufen sollen. Default: ['doccam']. */
  masterTrackCameras: CameraRole[];
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  profile: 'standard',
  mainResolution: '720p',
  // Welle 8c-pre30: 360p statt 240p — bei ≥360p greift der Hardware-VP9-Encoder
  // (vorher SW-Fallback „less than 360p"). Aspekt-korrigiert wird daraus bei der
  // 3:2-Doc-Cam 544×360. Kaum mehr Bandbreite bei niedriger previewFps, aber HW-codiert.
  previewResolution: '360p',
  mainFps: 30,
  previewFps: 8,
  hasPreview: true,
  webcamLabel: null,
  docCamLabel: null,
  audioInputLabel: 'ReSpeaker',
  masterTrackCameras: ['doccam'],
};

export interface DeviceMapping {
  webcamDeviceId: string;
  docCamDeviceId: string | null;
}

export function mapDevices(
  devices: { deviceId: string; label: string }[],
  settings: VideoSettings,
): DeviceMapping | null {
  if (devices.length === 0) return null;

  const lc = (s: string) => s.toLowerCase();
  let webcamDevice = settings.webcamLabel
    ? devices.find((d) => lc(d.label).includes(lc(settings.webcamLabel!)))
    : null;
  if (!webcamDevice) {
    if (settings.docCamLabel) {
      webcamDevice = devices.find((d) => !lc(d.label).includes(lc(settings.docCamLabel!)));
    } else {
      webcamDevice = devices[0];
    }
  }
  if (!webcamDevice) return null;

  const docCamDevice = settings.docCamLabel
    ? devices.find((d) => lc(d.label).includes(lc(settings.docCamLabel!)))
    : devices.find((d) => d.deviceId !== webcamDevice!.deviceId);

  return {
    webcamDeviceId: webcamDevice.deviceId,
    docCamDeviceId: docCamDevice?.deviceId ?? null,
  };
}

export function findAudioDevice(
  devices: { deviceId: string; label: string; kind: string }[],
  audioInputLabel: string | null,
): string | null {
  const audioInputs = devices.filter((d) => d.kind === 'audioinput');
  if (audioInputs.length === 0) return null;

  if (audioInputLabel) {
    const lc = audioInputLabel.toLowerCase();
    const match = audioInputs.find((d) => d.label.toLowerCase().includes(lc));
    if (match) return match.deviceId;
    console.warn(`[videoSettings] audioInputLabel "${audioInputLabel}" nicht gefunden`);
  }
  return audioInputs[0].deviceId;
}

export function isMasterTrackForRole(
  cameraRole: CameraRole,
  settings: VideoSettings,
): boolean {
  return settings.masterTrackCameras?.includes(cameraRole) ?? false;
}
